import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { LogIn } from "lucide-react";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password, remember);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="flex min-h-screen flex-col bg-[#F8FAFC] text-[var(--brand-ink)]"
      data-testid="page-login"
    >
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="login-home-link">
            <div className="flex flex-col leading-tight">
              <span className="font-display text-base font-black tracking-tight">
                LAST
              </span>
              <span className="label-kicker text-[var(--brand-body)]">
                Admin console
              </span>
            </div>
          </Link>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-5 py-16 sm:px-8">
        <div className="w-full max-w-md">
          <p className="label-kicker text-[#6CABDD]">Sign in</p>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Welcome back, gaffer.
          </h1>
          <p className="mt-2 text-sm text-[var(--brand-body)]">
            Log in to post updates to the LAST home page.
          </p>

          <form
            onSubmit={onSubmit}
            className="mt-8 space-y-4 rounded-md border border-black/10 bg-white p-6 sm:p-8"
            data-testid="login-form"
          >
            <label className="block">
              <span className="label-kicker text-[var(--brand-body)]">Email</span>
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="mt-2 block w-full rounded-md border border-black/15 bg-white px-4 py-2.5 font-display text-[var(--brand-ink)] outline-none transition-colors focus:border-[#0A0F17]"
              />
            </label>
            <label className="block">
              <span className="label-kicker text-[var(--brand-body)]">Password</span>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="mt-2 block w-full rounded-md border border-black/15 bg-white px-4 py-2.5 font-display text-[var(--brand-ink)] outline-none transition-colors focus:border-[#0A0F17]"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--brand-ink)]">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                data-testid="login-remember"
                className="h-4 w-4 cursor-pointer accent-[#0A0F17]"
              />
              <span>Keep me signed in for 30 days</span>
            </label>
            {error ? (
              <p
                className="rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm text-[var(--brand-ink)]"
                data-testid="login-error"
              >
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0A0F17] px-5 py-3 font-display text-sm font-bold uppercase tracking-wide text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
              <LogIn size={16} />
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-sm text-[var(--brand-body)]">
            <Link to="/" className="underline decoration-[#6CABDD] underline-offset-4">
              ← Back to LAST
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
